/**
 * Admin: Sidebar-Labels, Sichtbarkeit + Gruppe pro Tenant (2026-08-17, Gruppen 2026-09-17).
 *
 * Universeller Slug-Override-Editor fuer die Portal-Sidebar. Zeigt jede
 * bekannte Sidebar-Zeile (Modul-Toplevel, Sub-Eintraege wie Berichte/Aufgaben,
 * dynamisch registrierte Module aus /portal/modules/available, System-Items
 * wie Startseite/Kalender/Todos) und laesst pro Zeile den Anzeige-Text
 * ueberschreiben, die Zeile fuer den Tenant komplett ausblenden oder die
 * Arbeitsbereich-Gruppe waehlen (Joerg 2026-09-17).
 *
 * Seit 2026-09-30 (Regel „Dokument-Navigation" R5/MAT, Abhakliste 1.46): Änderungen
 * an Alias, Gruppe und Sichtbarkeit werden vorgemerkt und erst mit „Speichern (n)"
 * geschrieben (Muster ModulePermissionsMatrix). „Abbrechen" verwirft; der
 * Verlassen-Schutz des Portals fragt bei vorgemerkten Änderungen nach.
 *
 * Datenmodell: portal_sidebar_label_overrides (tenant_id, slug, alias, hidden, section).
 * Kein Override = Standard aus NAV_ITEMS / Modul-Registry.
 *
 * Master-Liste kommt vom Client: NAV_ITEMS (statische, hardcoded Items) +
 * /portal/modules/available (dynamisch registrierte Container). Der Server
 * kennt die Master-Liste nicht — er speichert nur was abweicht.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Info, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { apiFetch, registerDirtySource } from '@onvice/portal-shell-api';
import { EditBar, UnsavedChangesModal, useChangeMap, useDialogs, usePageLabel } from '@onvice/portal-ui';

import { NAV_ITEMS } from '../../components/PortalSidebar';
import {
  SIDEBAR_GROUPS, groupDef, isSidebarGroupSlug, mapNavSectionToGroup, resolveGroup,
  type SidebarGroupSlug, type SidebarOverride,
} from '../../lib/sidebar-groups';

interface MasterItem {
  slug: string;
  defaultLabel: string;
  defaultGroup: SidebarGroupSlug;
  source: 'static' | 'dynamic';
}

interface AvailableModuleResponse {
  slug: string;
  name: string;
  nav: { section: string; label: string } | null;
}

const NO_OVERRIDE: SidebarOverride = { alias: null, hidden: false, section: null };

function normalize(ov: Partial<SidebarOverride> | undefined): SidebarOverride {
  return { alias: ov?.alias ?? null, hidden: !!ov?.hidden, section: ov?.section ?? null };
}

export default function AdminSidebarLabelsPage() {
  usePageLabel('Sidebar-Einstellungen', 'other');
  const dlg = useDialogs();
  const [dynamicItems, setDynamicItems] = useState<MasterItem[]>([]);
  const [overrides, setOverrides] = useState<Record<string, SidebarOverride>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);

  const loadAll = useCallback(async (): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    try {
      const [ovRes, modRes] = await Promise.all([
        apiFetch<{ success: boolean; data: Record<string, Partial<SidebarOverride>> }>('/admin/sidebar-overrides'),
        apiFetch<{ success: boolean; data: AvailableModuleResponse[] }>('/portal/modules/available'),
      ]);
      const map: Record<string, SidebarOverride> = {};
      for (const [slug, ov] of Object.entries(ovRes.data ?? {})) map[slug] = normalize(ov);
      setOverrides(map);
      // Dynamische Module (die nicht schon in NAV_ITEMS stehen) als MasterItems.
      const staticSlugs = new Set(NAV_ITEMS.map((it) => it.slug));
      const dyn: MasterItem[] = (modRes.data ?? [])
        .filter((m) => m.nav !== null && !staticSlugs.has(m.slug))
        .map((m) => ({
          slug: m.slug,
          defaultLabel: m.nav!.label || m.name,
          defaultGroup: mapNavSectionToGroup(m.nav!.section, m.slug),
          source: 'dynamic' as const,
        }));
      setDynamicItems(dyn);
    } catch (e) {
      setLoadError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void loadAll(); }, [loadAll]);

  // Vorgemerkte Änderungen je Slug — geschrieben wird erst mit „Speichern (n)".
  const baselineOf = useCallback((slug: string): SidebarOverride => overrides[slug] ?? NO_OVERRIDE, [overrides]);
  const changes = useChangeMap<SidebarOverride>(baselineOf);

  useEffect(() => registerDirtySource({
    id: 'sidebar-overrides',
    label: 'Sidebar-Einstellungen',
    isDirty: () => changes.dirty,
    discard: changes.reset,
  }), [changes.dirty, changes.reset]);

  // Master-Liste (statisch + dynamisch), Reihenfolge folgt NAV_ITEMS.
  const master: MasterItem[] = useMemo(() => {
    const staticItems: MasterItem[] = NAV_ITEMS.map((it) => ({
      slug: it.slug,
      defaultLabel: it.label,
      defaultGroup: it.group,
      source: 'static' as const,
    }));
    return [...staticItems, ...dynamicItems];
  }, [dynamicItems]);

  // Gruppierung nach der GESPEICHERTEN Gruppe (Override > Default), Reihenfolge SIDEBAR_GROUPS.
  // Die Zeile wandert erst nach „Speichern" in ihre neue Gruppe — sonst springt sie unter
  // dem Mauszeiger weg.
  const grouped = useMemo(() => {
    const out = new Map<SidebarGroupSlug, MasterItem[]>();
    for (const g of SIDEBAR_GROUPS) out.set(g.slug, []);
    for (const m of master) {
      const eff = resolveGroup(m.defaultGroup, overrides[m.slug]?.section);
      out.get(eff)!.push(m);
    }
    return out;
  }, [master, overrides]);

  function patchRow(slug: string, patch: Partial<SidebarOverride>): void {
    const current = changes.get(slug) ?? NO_OVERRIDE;
    changes.set(slug, { ...current, ...patch });
  }

  async function save(): Promise<void> {
    if (!changes.dirty) return;
    setSaving(true);
    setMessage(null);
    const failed: string[] = [];
    const saved: Record<string, SidebarOverride | null> = {};
    for (const [slug, value] of changes.changes) {
      try {
        const res = await apiFetch<{ success: boolean; data: SidebarOverride & { slug: string } }>(
          `/admin/sidebar-overrides/${encodeURIComponent(slug)}`,
          { method: 'PATCH', body: JSON.stringify({ alias: value.alias?.trim() || null, hidden: value.hidden, section: value.section ?? null }) },
        );
        // Server-Antwort als Wahrheit übernehmen.
        const d = normalize(res.data);
        const isDefault = d.alias === null && d.hidden === false && d.section === null;
        saved[slug] = isDefault ? null : d;
      } catch (e) {
        failed.push(`${slug}: ${(e as Error).message}`);
      }
    }
    setOverrides((prev) => {
      const next = { ...prev };
      for (const [slug, v] of Object.entries(saved)) {
        if (v === null) delete next[slug]; else next[slug] = v;
      }
      return next;
    });
    // Gespeicherte Änderungen entfallen über den neuen Ausgangsstand (pruneChanges);
    // gescheiterte bleiben vorgemerkt.
    setSaving(false);
    if (failed.length > 0) {
      await dlg.notify({ kind: 'error', title: 'Nicht alle Zeilen gespeichert', message: 'Die übrigen Änderungen bleiben vorgemerkt — bitte erneut speichern.', items: failed });
    } else {
      setMessage('Gespeichert. Änderungen greifen beim nächsten Login (die Sidebar liest die Einstellungen einmal pro Sitzung).');
    }
  }

  function cancel(): void {
    if (changes.dirty) setAsking(true);
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6" data-test-anchor="sidebar-einstellungen" data-mode="edit">
      <div>
        <h1 className="text-2xl font-semibold text-slate-100 mb-2">Sidebar: Beschriftung, Sichtbarkeit &amp; Gruppe</h1>
        <p className="text-slate-400 text-sm max-w-3xl">
          Pro Sidebar-Zeile kannst du einen eigenen Anzeige-Text festlegen, die Zeile
          für alle Nutzer dieses Mandanten ausblenden oder sie einer anderen Gruppe
          zuordnen. Alias leer, sichtbar und Gruppe „Standard" = Vorgabe aus dem Modul.
          Änderungen werden vorgemerkt und erst mit „Speichern" geschrieben.
        </p>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs text-slate-400 flex gap-2">
        <Info className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
        <div>
          <b>Vorrang:</b> Einstellung hier &gt; Modul-Container &gt; Vorgabe im Portal. Das gilt für Text und Gruppe.
          Die Tabelle ist nach der gespeicherten Gruppe sortiert — nach dem Speichern wandert die Zeile in ihre neue Gruppe.
          Änderungen werden beim nächsten Login übernommen.
        </div>
      </div>

      {message && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-200" data-test-anchor="sidebar-gespeichert">
          {message}
        </div>
      )}
      {loadError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200 flex flex-wrap items-center gap-3" data-test-anchor="sidebar-ladefehler">
          <span>Nicht geladen: {loadError}</span>
          <button type="button" onClick={() => void loadAll()} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-500/40 text-xs hover:bg-red-500/10">
            <RefreshCw size={12} /> Erneut laden
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Lade Sidebar-Einträge …
        </div>
      ) : (
        SIDEBAR_GROUPS.map((g) => {
          const items = grouped.get(g.slug) ?? [];
          if (items.length === 0) return null;
          const GIcon = g.icon;
          return (
            <section key={g.slug} className="rounded-xl border border-slate-700 overflow-hidden" data-test-anchor={`sidebar-group-${g.slug}`}>
              <div className="bg-slate-800/60 px-3 py-2 text-sm font-medium text-slate-100 flex items-center gap-2">
                <GIcon size={15} className="text-primary" /> {g.label}
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-900/60 text-slate-400 text-xs">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-medium w-44">Kennung</th>
                    <th className="text-left px-3 py-1.5 font-medium">Standard</th>
                    <th className="text-left px-3 py-1.5 font-medium">Alias (Mandant)</th>
                    <th className="text-left px-3 py-1.5 font-medium w-52">Gruppe</th>
                    <th className="text-center px-3 py-1.5 font-medium w-24">Sichtbar</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => {
                    const ov = changes.get(row.slug) ?? NO_OVERRIDE;
                    const rowDirty = changes.isChanged(row.slug);
                    const sectionValue = isSidebarGroupSlug(ov.section) ? ov.section : '';
                    return (
                      <tr key={row.slug} className={`border-t border-slate-700 ${rowDirty ? 'bg-amber-500/5' : ''}`} data-test-anchor={`sidebar-row-${row.slug}`} data-state={rowDirty ? 'dirty' : undefined}>
                        <td className="px-3 py-1.5 font-mono text-xs text-slate-400 align-middle">
                          {row.slug}
                          {row.source === 'dynamic' && (
                            <span className="ml-2 text-[10px] px-1 py-0.5 rounded bg-primary/20 text-primary uppercase tracking-wider" title="vom Modul-Container registriert">dyn</span>
                          )}
                          {rowDirty && <span className="ml-2 text-[10px] text-amber-300">geändert</span>}
                        </td>
                        <td className="px-3 py-1.5 align-middle">{row.defaultLabel}</td>
                        <td className="px-3 py-1.5 align-middle">
                          <input
                            type="text"
                            value={ov.alias ?? ''}
                            placeholder="(Standard übernehmen)"
                            maxLength={60}
                            disabled={saving}
                            onChange={(e) => patchRow(row.slug, { alias: e.target.value === '' ? null : e.target.value })}
                            className="w-full rounded border border-slate-700 bg-slate-900/60 px-2 py-1 text-sm focus:border-primary outline-none disabled:opacity-50"
                          />
                        </td>
                        <td className="px-3 py-1.5 align-middle">
                          <select
                            value={sectionValue}
                            disabled={saving}
                            onChange={(e) => patchRow(row.slug, { section: e.target.value === '' ? null : e.target.value })}
                            title="Arbeitsbereich-Gruppe in der Sidebar"
                            className="w-full rounded border border-slate-700 bg-slate-900/60 px-2 py-1 text-sm focus:border-primary outline-none disabled:opacity-50"
                          >
                            <option value="">Standard ({groupDef(row.defaultGroup).label})</option>
                            {SIDEBAR_GROUPS.map((opt) => (
                              <option key={opt.slug} value={opt.slug}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-1.5 text-center align-middle">
                          <button
                            type="button"
                            onClick={() => patchRow(row.slug, { hidden: !ov.hidden })}
                            disabled={saving}
                            aria-pressed={!ov.hidden}
                            title={ov.hidden ? 'Ausgeblendet — Klick blendet ein' : 'Sichtbar — Klick blendet aus'}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded border transition-colors disabled:opacity-50 ${
                              ov.hidden
                                ? 'border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20'
                                : 'border-slate-700 text-slate-400 hover:text-slate-200'
                            }`}
                          >
                            {ov.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          );
        })
      )}

      {!loading && !loadError && (
        <EditBar
          editing
          dirty={changes.dirty}
          saving={saving}
          changeCount={changes.count}
          onEdit={() => undefined}
          onSave={() => { void save(); }}
          onCancel={cancel}
          sticky
          testAnchor="sidebar-edit-bar"
        />
      )}
      <UnsavedChangesModal
        open={asking}
        label="Sidebar-Einstellungen"
        onStay={() => setAsking(false)}
        onDiscard={() => { setAsking(false); changes.reset(); setMessage(null); }}
      />
    </div>
  );
}
